var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckBox, CheckBoxOutlineBlank, IndeterminateCheckBox } from '@mui/icons-material';
import { Autocomplete, Box, Checkbox, Chip, TextField, Typography, styled } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import useTranslate from '../../../localization/helpers';
var icon = _jsx(CheckBoxOutlineBlank, { fontSize: 'small' });
var checkedIcon = _jsx(CheckBox, { fontSize: 'small' });
var indeterminateIcon = _jsx(IndeterminateCheckBox, { fontSize: 'small' });
var TreeNodeBox = styled(Box)(function (_a) {
    var depth = _a.depth;
    return ({
        paddingLeft: depth * 24,
        display: 'flex',
        alignItems: 'center',
        width: '100%'
    });
});
export var MachineModelTreeAutocomplete = function (_a) {
    var machines = _a.machines, value = _a.value, onChange = _a.onChange, label = _a.label, _b = _a.placeholder, placeholder = _b === void 0 ? '' : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.error, error = _d === void 0 ? false : _d, helperText = _a.helperText, _e = _a.size, size = _e === void 0 ? 'small' : _e, _f = _a.limitTags, limitTags = _f === void 0 ? 2 : _f, _g = _a.excludeOthers, excludeOthers = _g === void 0 ? true : _g;
    var translate = useTranslate().translate;
    var _h = __read(useState(false), 2), open = _h[0], setOpen = _h[1];
    // Convert flat machines list to tree structure
    var treeNodes = useMemo(function () {
        var nodes = [];
        machines.forEach(function (machine) {
            var models = machine.models.filter(function (model) { return !excludeOthers || model.modelNumber !== 'Others'; });
            var machineNode = {
                id: machine.id,
                label: machine.name,
                type: 'machine',
                machineId: machine.id,
                children: models.length > 0
                    ? models.map(function (model) { return ({
                        id: "".concat(machine.id, "-").concat(model.modelNumber),
                        label: model.modelNumber === 'Others'
                            ? translate('common.others')
                            : "".concat(model.modelNumber),
                        type: 'model',
                        machineId: machine.id,
                        modelNumber: model.modelNumber
                    }); })
                    : undefined // No children for machines without models
            };
            nodes.push(machineNode);
        });
        return nodes;
    }, [machines, translate, excludeOthers]);
    // Flatten tree for autocomplete options
    var flatOptions = useMemo(function () {
        var options = [];
        treeNodes.forEach(function (machine) {
            options.push(machine);
            if (machine.children) {
                options.push.apply(options, __spreadArray([], __read(machine.children), false));
            }
        });
        return options;
    }, [treeNodes]);
    // Get selected options from value
    var selectedOptions = useMemo(function () {
        var selected = [];
        value.forEach(function (selection) {
            var machineNode = treeNodes.find(function (n) { return n.id === selection.machineId; });
            if (!machineNode)
                return;
            // If machine has no children (no models), add the machine node itself
            if (!machineNode.children || machineNode.children.length === 0) {
                if (selection.modelNumbers.length === 0) {
                    selected.push(machineNode);
                }
                return;
            }
            // Always add individual model nodes that are selected
            selection.modelNumbers.forEach(function (modelNum) {
                var _a;
                var modelNode = (_a = machineNode.children) === null || _a === void 0 ? void 0 : _a.find(function (c) { return c.modelNumber === modelNum; });
                if (modelNode) {
                    selected.push(modelNode);
                }
            });
            // Also add machine node if all models are selected
            if (machineNode.children &&
                selection.modelNumbers.length === machineNode.children.length &&
                machineNode.children.every(function (child) {
                    return selection.modelNumbers.includes(child.modelNumber || '');
                })) {
                selected.push(machineNode);
            }
        });
        return selected;
    }, [value, treeNodes]);
    // Calculate node state (checked/indeterminate)
    var getNodeState = useCallback(function (node) {
        if (node.type === 'model') {
            return selectedOptions.some(function (opt) { return opt.id === node.id; });
        }
        // Machine node
        // Check if this machine has no models (children)
        if (!node.children || node.children.length === 0) {
            // For machines without models, check if the machine itself is selected
            var isMachineSelected_1 = selectedOptions.some(function (opt) { return opt.id === node.id && opt.type === 'machine'; });
            return { checked: isMachineSelected_1, indeterminate: false };
        }
        // Machine with models
        var selectedModels = selectedOptions.filter(function (opt) { return opt.type === 'model' && opt.machineId === node.id; });
        var isMachineSelected = selectedOptions.some(function (opt) { return opt.id === node.id && opt.type === 'machine'; });
        var totalModels = node.children.length;
        if (isMachineSelected || selectedModels.length === totalModels) {
            return { checked: true, indeterminate: false };
        }
        if (selectedModels.length > 0) {
            return { checked: false, indeterminate: true };
        }
        return { checked: false, indeterminate: false };
    }, [selectedOptions]);
    // Handle selection change - properly handle parent-child selection/deselection
    var handleChange = useCallback(function (_event, newValue, reason, details) {
        var clickedOption = details === null || details === void 0 ? void 0 : details.option;
        if (!clickedOption) {
            // If no specific option was clicked, just update based on newValue
            var selections_1 = [];
            var machineMap_1 = new Map();
            newValue.forEach(function (option) {
                var _a;
                if (option.type === 'machine') {
                    if (!machineMap_1.has(option.id)) {
                        machineMap_1.set(option.id, new Set());
                    }
                    var machine = machines.find(function (m) { return m.id === option.id; });
                    machine === null || machine === void 0 ? void 0 : machine.models.forEach(function (model) {
                        var _a;
                        (_a = machineMap_1.get(option.id)) === null || _a === void 0 ? void 0 : _a.add(model.modelNumber);
                    });
                }
                else if (option.type === 'model' && option.machineId) {
                    if (!machineMap_1.has(option.machineId)) {
                        machineMap_1.set(option.machineId, new Set());
                    }
                    if (option.modelNumber) {
                        (_a = machineMap_1.get(option.machineId)) === null || _a === void 0 ? void 0 : _a.add(option.modelNumber);
                    }
                }
            });
            machineMap_1.forEach(function (modelNumbers, machineId) {
                selections_1.push({
                    machineId: machineId,
                    modelNumbers: Array.from(modelNumbers)
                });
            });
            onChange(selections_1);
            return;
        }
        // Handle clicking on a machine node (parent)
        if (clickedOption.type === 'machine') {
            var machine = machines.find(function (m) { return m.id === clickedOption.id; });
            if (!machine)
                return;
            // Check if machine has no models
            if (machine.models.length === 0) {
                // For machines without models, toggle selection of the machine itself
                var currentSelection_1 = value.find(function (v) { return v.machineId === clickedOption.id; });
                if (currentSelection_1) {
                    // Deselect the machine
                    onChange(value.filter(function (v) { return v.machineId !== clickedOption.id; }));
                }
                else {
                    // Select the machine with empty modelNumbers array
                    onChange(__spreadArray(__spreadArray([], __read(value), false), [{ machineId: clickedOption.id, modelNumbers: [] }], false));
                }
                return;
            }
            // Check current state for machines with models
            var currentSelection = value.find(function (v) { return v.machineId === clickedOption.id; });
            var hasAnySelected = currentSelection && currentSelection.modelNumbers.length > 0;
            if (hasAnySelected) {
                // If any models are selected (either some or all), deselect all
                onChange(value.filter(function (v) { return v.machineId !== clickedOption.id; }));
            }
            else {
                // If no models selected, select all models for this machine
                var newSelections = value.filter(function (v) { return v.machineId !== clickedOption.id; });
                newSelections.push({
                    machineId: clickedOption.id,
                    modelNumbers: machine.models.map(function (m) { return m.modelNumber; })
                });
                onChange(newSelections);
            }
            return;
        }
        // Handle clicking on a model node (child)
        if (clickedOption.type === 'model' && clickedOption.machineId && clickedOption.modelNumber) {
            var currentSelection = value.find(function (v) { return v.machineId === clickedOption.machineId; });
            var isModelSelected = currentSelection === null || currentSelection === void 0 ? void 0 : currentSelection.modelNumbers.includes(clickedOption.modelNumber);
            if (isModelSelected) {
                // Deselect this model
                var newModelNumbers_1 = currentSelection.modelNumbers.filter(function (m) { return m !== clickedOption.modelNumber; });
                if (newModelNumbers_1.length === 0) {
                    // Remove the machine entry if no models left
                    onChange(value.filter(function (v) { return v.machineId !== clickedOption.machineId; }));
                }
                else {
                    // Update with remaining models
                    onChange(value.map(function (v) {
                        return v.machineId === clickedOption.machineId
                            ? __assign(__assign({}, v), { modelNumbers: newModelNumbers_1 }) : v;
                    }));
                }
            }
            else if (currentSelection) {
                onChange(value.map(function (v) {
                    return v.machineId === clickedOption.machineId
                        ? __assign(__assign({}, v), { modelNumbers: __spreadArray(__spreadArray([], __read(v.modelNumbers), false), [clickedOption.modelNumber], false) }) : v;
                }));
            }
            else {
                onChange(__spreadArray(__spreadArray([], __read(value), false), [
                    { machineId: clickedOption.machineId, modelNumbers: [clickedOption.modelNumber] }
                ], false));
            }
        }
    }, [machines, onChange, value]);
    // Render option
    var renderOption = function (props, option) {
        var depth = option.type === 'machine' ? 0 : 1;
        var nodeState = getNodeState(option);
        var stateObj = typeof nodeState === 'boolean' ? { checked: nodeState, indeterminate: false } : nodeState;
        return (_createElement("li", __assign({}, props, { key: option.id }),
            _jsxs(TreeNodeBox, { depth: depth, children: [_jsx(Checkbox, { icon: icon, checkedIcon: checkedIcon, indeterminateIcon: indeterminateIcon, style: { marginRight: 8 }, checked: stateObj.checked, indeterminate: stateObj.indeterminate }), _jsx(Typography, { variant: option.type === 'machine' ? 'body1' : 'body2', sx: {
                            fontWeight: option.type === 'machine' ? 600 : 400,
                            color: option.type === 'machine' ? 'text.primary' : 'text.secondary'
                        }, children: option.label })] })));
    };
    // Custom filter to show parent+children when either matches
    var filterOptions = useCallback(function (options, state) {
        var searchText = state.inputValue.toLowerCase().trim();
        if (!searchText) {
            return options;
        }
        var matchedNodes = new Set();
        // First pass: identify which machines and models match
        treeNodes.forEach(function (machine) {
            var _a, _b, _c;
            var machineMatches = machine.label.toLowerCase().includes(searchText);
            if (machineMatches) {
                // If machine matches, include machine and all its children
                matchedNodes.add(machine.id);
                (_a = machine.children) === null || _a === void 0 ? void 0 : _a.forEach(function (child) {
                    matchedNodes.add(child.id);
                });
            }
            else {
                // Check if any child model matches
                var hasMatchingChild = (_b = machine.children) === null || _b === void 0 ? void 0 : _b.some(function (child) {
                    return child.label.toLowerCase().includes(searchText);
                });
                if (hasMatchingChild) {
                    // Include machine and matching children
                    matchedNodes.add(machine.id);
                    (_c = machine.children) === null || _c === void 0 ? void 0 : _c.forEach(function (child) {
                        if (child.label.toLowerCase().includes(searchText)) {
                            matchedNodes.add(child.id);
                        }
                    });
                }
            }
        });
        // Filter options to only include matched nodes
        return options.filter(function (option) { return matchedNodes.has(option.id); });
    }, [treeNodes]);
    // Render tags (selected items) - show model numbers as chips, or machine name for machines without models
    var renderValue = function (tagValue, getTagProps) {
        // Collect all selected model numbers and machines without models
        var uniqueItems = new Map();
        tagValue.forEach(function (option) {
            if (option.type === 'model' && option.modelNumber) {
                var key = "".concat(option.machineId, "-").concat(option.modelNumber);
                var machineNode = treeNodes.find(function (n) { return n.id === option.machineId; });
                if (machineNode && !uniqueItems.has(key)) {
                    uniqueItems.set(key, {
                        machineName: machineNode.label,
                        modelNumber: option.modelNumber,
                        isMachineOnly: false
                    });
                }
            }
            else if (option.type === 'machine') {
                if (option.children && option.children.length > 0) {
                    // If machine has models and is selected, add all its models
                    option.children.forEach(function (child) {
                        if (child.modelNumber) {
                            var key = "".concat(option.id, "-").concat(child.modelNumber);
                            if (!uniqueItems.has(key)) {
                                uniqueItems.set(key, {
                                    machineName: option.label,
                                    modelNumber: child.modelNumber,
                                    isMachineOnly: false
                                });
                            }
                        }
                    });
                }
                else {
                    // Machine without models - show machine name as chip
                    var key = "".concat(option.id, "-machine");
                    if (!uniqueItems.has(key)) {
                        uniqueItems.set(key, {
                            machineName: option.label,
                            isMachineOnly: true
                        });
                    }
                }
            }
        });
        return Array.from(uniqueItems.entries()).map(function (_a, index) {
            var _b = __read(_a, 2), key = _b[0], item = _b[1];
            return (_createElement(Chip, __assign({}, getTagProps({ index: index }), { key: key, label: item.isMachineOnly
                    ? item.machineName
                    : item.modelNumber === 'Others'
                        ? translate('common.others')
                        : item.modelNumber, size: 'small', variant: 'soft' })));
        });
    };
    return (_jsx(Autocomplete, { multiple: true, limitTags: limitTags, size: size, open: open, onOpen: function () { return setOpen(true); }, onClose: function () { return setOpen(false); }, value: selectedOptions, onChange: handleChange, options: flatOptions, filterOptions: filterOptions, disableCloseOnSelect: true, disabled: disabled, getOptionLabel: function (option) { return option.label; }, isOptionEqualToValue: function (option, value) { return option.id === value.id; }, renderOption: renderOption, renderValue: renderValue, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { label: label, placeholder: placeholder, error: error, helperText: helperText }))); }, sx: {
            '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
                    padding: '3.5px 4px 3.5px 8px'
                }
            }
        } }));
};
